import { LISTING_PRICE_SETTINGS_PATH } from '@auctisync/api-client';
import { useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { ToastProvider } from './components/Toast';
import { supabase } from './lib/supabase';
import { BidderHousesProvider } from './hooks/useBidderHouses';
import { useCapacitorDeepLinkAuth } from './hooks/useCapacitorDeepLinkAuth';
import { ConnectionProvider } from './hooks/useConnection';
import { AccountLayout } from './routes/layouts/AccountLayout';
import { AccountOverview } from './routes/pages/AccountOverview';
import { AccountPickup } from './routes/pages/AccountPickup';
import { LiveNowRedirect } from './routes/pages/LiveNowRedirect';
import { PublicHelp } from './routes/pages/PublicHelp';
import { PublicRegister } from './routes/pages/PublicRegister';
import { PublicSearch } from './routes/pages/PublicSearch';
import { PublicWatchlist } from './routes/pages/PublicWatchlist';
import { ProtectedRoute } from './routes/guards/ProtectedRoute';
import { AdminChromeLayout } from './routes/layouts/AdminChromeLayout';
import { AdminLayout } from './routes/layouts/AdminLayout';
import { PublicLayout } from './routes/layouts/PublicLayout';
import { SuperAdminLayout } from './routes/layouts/SuperAdminLayout';
import { AdminAiSettings } from './routes/pages/AdminAiSettings';
import { AdminBranding } from './routes/pages/AdminBranding';
import { AdminOnboarding } from './routes/pages/AdminOnboarding';
import { AdminBidderImport } from './routes/pages/AdminBidderImport';
import { AdminSalesIntel } from './routes/pages/AdminSalesIntel';
import { AdminAuctionApprovals } from './routes/pages/AdminAuctionApprovals';
import { AdminAuctionDetail } from './routes/pages/AdminAuctionDetail';
import { AdminAuctionList } from './routes/pages/AdminAuctionList';
import { AdminBidders } from './routes/pages/AdminBidders';
import { AdminLotQuestions } from './routes/pages/AdminLotQuestions';
import { AdminBidderDetail } from './routes/pages/AdminBidderDetail';
import { AdminAuctioneerKeys } from './routes/pages/AdminAuctioneerKeys';
import { AdminAuctioneerConsole } from './routes/pages/AdminAuctioneerConsole';
import { AdminBilling } from './routes/pages/AdminBilling';
import { AdminInvoices } from './routes/pages/AdminInvoices';
import { AdminInvoiceDetail } from './routes/pages/AdminInvoiceDetail';
import { AdminHouseContact } from './routes/pages/AdminHouseContact';
import { AdminInvoiceSettings } from './routes/pages/AdminInvoiceSettings';
import { InvoicePrint } from './routes/pages/InvoicePrint';
import { AdminInvoiceFailedReport } from './routes/pages/AdminInvoiceFailedReport';
import { AdminBrief } from './routes/pages/AdminBrief';
import { AdminCatalog } from './routes/pages/AdminCatalog';
import { AdminConsignorDetail } from './routes/pages/AdminConsignorDetail';
import { AdminConsignors } from './routes/pages/AdminConsignors';
import { AdminCopilot } from './routes/pages/AdminCopilot';
import { AdminCopilotMemory } from './routes/pages/AdminCopilotMemory';
import { AdminDashboard } from './routes/pages/AdminDashboard';
import { AdminReports } from './routes/pages/AdminReports';
import { AdminForgotPassword } from './routes/pages/AdminForgotPassword';
import { AdminInventoryDetail } from './routes/pages/AdminInventoryDetail';
import { AdminInventoryImport } from './routes/pages/AdminInventoryImport';
import { AdminInventoryList } from './routes/pages/AdminInventoryList';
import { AdminLotDetail } from './routes/pages/AdminLotDetail';
import { AdminLotGrid } from './routes/pages/AdminLotGrid';
import { AdminSignIn } from './routes/pages/AdminSignIn';
import { AdminTaxonomies } from './routes/pages/AdminTaxonomies';
import { AdminConsignmentProfiles } from './routes/pages/AdminConsignmentProfiles';
import { AdminTaxProfiles } from './routes/pages/AdminTaxProfiles';
import { AuthCallback } from './routes/pages/AuthCallback';
import { AcceptInvite } from './routes/pages/AcceptInvite';
import { AuthConfirm } from './routes/pages/AuthConfirm';
import { BidderDashboard } from './routes/pages/BidderDashboard';
import { BidderInvoiceDetail } from './routes/pages/BidderInvoiceDetail';
import { BidderInvoices } from './routes/pages/BidderInvoices';
import { BidderPaymentMethods } from './routes/pages/BidderPaymentMethods';
import { BidderProfile } from './routes/pages/BidderProfile';
import { DesignShowcase } from './routes/pages/DesignShowcase';
import { NotFound } from './routes/pages/NotFound';
import { PublicAuctionDetail } from './routes/pages/PublicAuctionDetail';
import { PublicAuctionList } from './routes/pages/PublicAuctionList';
import { PreviewFrame } from './routes/preview/PreviewFrame';
import { BidderHome } from './routes/pages/BidderHome';
import { BidderForgotPassword } from './routes/pages/BidderForgotPassword';
import { BidderSignIn } from './routes/pages/BidderSignIn';
import { BidderSignUp } from './routes/pages/BidderSignUp';
import { PublicLiveRoom } from './routes/pages/PublicLiveRoom';
import { PublicLotDetail } from './routes/pages/PublicLotDetail';
import { ResetPassword } from './routes/pages/ResetPassword';
import { SuperAppReports } from './routes/pages/SuperAppReports';
import { SuperBriefs } from './routes/pages/SuperBriefs';
import { SuperChurnSignals } from './routes/pages/SuperChurnSignals';
import { SuperCopilotGaps } from './routes/pages/SuperCopilotGaps';
import { SuperHelpFeedback } from './routes/pages/SuperHelpFeedback';
import { SuperCompetitorResearch } from './routes/pages/SuperCompetitorResearch';
import { SuperDashboard } from './routes/pages/SuperDashboard';
import { SuperEngineTelemetry } from './routes/pages/SuperEngineTelemetry';
import { SuperErrorClusters } from './routes/pages/SuperErrorClusters';
import { SuperOnboardingReset } from './routes/pages/SuperOnboardingReset';
import { SuperTenantInvites } from './routes/pages/SuperTenantInvites';
import { SuperPricingPredictions } from './routes/pages/SuperPricingPredictions';
import { SuperSalesAnalytics } from './routes/pages/SuperSalesAnalytics';
import { SuperForgotPassword } from './routes/pages/SuperForgotPassword';
import { SuperSignIn } from './routes/pages/SuperSignIn';
import { Unauthorized } from './routes/pages/Unauthorized';

/**
 * Route map:
 *
 *   /                              PublicLayout  (bidder theme · placeholder home)
 *   /design                        PublicLayout  (design-system showcase)
 *   /unauthorized                  PublicLayout  (role-denied landing)
 *   /auth/callback                 PublicLayout  (email-link return handler)
 *   /auth/reset-password           PublicLayout  (set new password after recovery)
 *
 *   /admin                         AdminLayout          (sign-in · no chrome)
 *   /admin/forgot-password         AdminLayout          (forgot password · no chrome)
 *   /admin/dashboard               AdminChromeLayout    (protected · nav + outlet)
 *   /admin/inventory               AdminChromeLayout    (list)
 *   /admin/inventory/:id           AdminChromeLayout    (detail · `:id` may be `new`)
 *   /admin/taxonomies              AdminChromeLayout    (categories + scales)
 *   /admin/consignors              AdminChromeLayout    (list)
 *   /admin/consignors/:id          AdminChromeLayout    (detail · `:id` may be `new`)
 *
 *   /super                         SuperAdminLayout  (super admin sign-in)
 *   /super/dashboard               SuperAdminLayout  (protected · requires super_admin)
 *
 *   *                              NotFound
 *
 * Hostname-based routing (e.g. `{tenant-domain}/admin` vs
 * `app.auctisync.com/admin`) is handled by edge rewrites in a later module.
 */
/**
 * Mounts the Capacitor deep-link auth listener inside the router so the
 * `auctisync://auth/callback` handoff from iOS / Android can rewrite the
 * in-app URL + trigger React Router navigation. No-op on web. Module #28
 * chunk 3.
 */
function DeepLinkBridge() {
  useCapacitorDeepLinkAuth();
  return null;
}

/**
 * Global password-recovery router. A reset-email link makes the Supabase
 * client fire a `PASSWORD_RECOVERY` auth event once it consumes the recovery
 * session — regardless of which URL the link actually landed on (the site
 * root, /auth/callback, or wherever the Supabase redirect allow-list sent it).
 * We catch that event app-wide and send the user to the set-a-new-password
 * page, so a valid reset link ALWAYS reaches the reset form instead of
 * silently dropping the user, logged-in, on the home page. Renders nothing.
 */
function PasswordRecoveryBridge() {
  const navigate = useNavigate();
  useEffect(() => {
    const { data } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        void navigate('/auth/reset-password', { replace: true });
      }
    });
    return () => data.subscription.unsubscribe();
  }, [navigate]);
  return null;
}

export function App() {
  return (
    <BrowserRouter>
      <ToastProvider>
        <DeepLinkBridge />
        <PasswordRecoveryBridge />
        <Routes>
          {/* Public + bidder surface */}
          <Route
            element={
              <BidderHousesProvider>
                {/* Connection state wraps the whole bidder surface, not just the
                    shell: the strip lives in the nav, but a lot card, a
                    countdown and the live room all have to know when the
                    numbers stopped being true. */}
                <ConnectionProvider>
                  <PublicLayout />
                </ConnectionProvider>
              </BidderHousesProvider>
            }
          >
            {/* Root is PUBLIC (2026-09-03, web design handoff). It used to be
                gated, so a visitor following a link to a house's own site was
                met with a login form before being told whose site it was — on a
                white-label surface, the worst possible first screen. BidderHome
                still does the multi-house routing for a signed-in bidder. */}
            <Route index element={<BidderHome />} />
            <Route path="signin" element={<BidderSignIn />} />
            <Route path="signup" element={<BidderSignUp />} />
            <Route path="forgot-password" element={<BidderForgotPassword />} />
            <Route path="auctions" element={<PublicAuctionList />} />
            {/* 🔴 BROWSE WAS MERGED INTO THE LIVE PAGE (Jim, 2026-09-14): *"There
                is no need to have Browse and Live."* The path stays so existing
                links, emails and bookmarks keep working — it resolves the open
                sale and lands on the one page, carrying any `?sale=` slug. */}
            <Route path="browse" element={<LiveNowRedirect />} />
            <Route path="live" element={<LiveNowRedirect />} />
            <Route path="search" element={<PublicSearch />} />
            <Route path="watchlist" element={<PublicWatchlist />} />
            <Route path="register" element={<PublicRegister />} />
            <Route path="help" element={<PublicHelp />} />
            <Route path="help/:slug" element={<PublicHelp />} />
            <Route path="auctions/:slug" element={<PublicAuctionDetail />} />
            <Route path="auctions/:slug/live" element={<PublicLiveRoom />} />
            <Route path="auctions/:slug/lots/:lotNumber" element={<PublicLotDetail />} />
            {/* THE ACCOUNT IS ONE PAGE WITH A RAIL (2026-09-03, web design
                handoff) — but still SIX ROUTES behind it. `/my/invoices/:id`
                has to be linkable from an email, a notification and the sale
                list, and a tabbed single route would break every one of those
                links. `AccountLayout` is chrome around real routes, not a state
                machine pretending to be one. */}
            <Route element={<AccountLayout />}>
              <Route path="my/dashboard" element={<AccountOverview />} />
              <Route path="my/bidding" element={<BidderDashboard />} />
              <Route path="my/pickup" element={<AccountPickup />} />
              <Route path="my/profile" element={<BidderProfile />} />
              <Route path="my/invoices" element={<BidderInvoices />} />
              <Route path="my/invoices/:id" element={<BidderInvoiceDetail />} />
              <Route path="my/payment-methods" element={<BidderPaymentMethods />} />
            </Route>
            <Route path="design" element={<DesignShowcase />} />
            <Route path="unauthorized" element={<Unauthorized />} />
            <Route path="auth/callback" element={<AuthCallback />} />
            {/* Auth email links land HERE, not on supabase.co — a From/link
                domain mismatch is a phishing signature and Gmail was silently
                dropping the mail. See AuthConfirm for the full account. */}
            <Route path="auth/confirm" element={<AuthConfirm />} />
            <Route path="auth/reset-password" element={<ResetPassword />} />
            {/* An invited owner has no password yet — AuthConfirm forces every
                type=invite link through here before anything else. */}
            <Route path="auth/accept-invite" element={<AcceptInvite />} />
          </Route>

          {/* Chrome-free bidder print surface — opens in a new tab and auto-prints,
              so it renders standalone (no PublicLayout header/footer). Auth is
              gated server-side by get_invoice_print_context + the page itself. */}
          <Route path="my/invoices/:id/print" element={<InvoicePrint />} />

          {/* Onboarding preview surface (spec §4, Phase E1) — a draft tenant's
              storefront rendered through the owner's own session so they can
              review it before going live. Standalone (own PreviewFrame chrome,
              not the bidder PublicLayout). Authz is enforced by RLS inside
              PreviewFrame (resolving the tenant by slug requires owner/staff). */}
          <Route path="preview/:tenantSlug" element={<PreviewFrame />}>
            <Route index element={<PublicAuctionList />} />
            <Route path="auctions/:slug" element={<PublicAuctionDetail />} />
          </Route>

          {/* Tenant staff admin surface */}
          <Route path="admin">
            {/* Auth pages — bare AdminLayout (no chrome) */}
            <Route element={<AdminLayout />}>
              <Route index element={<AdminSignIn />} />
              <Route path="forgot-password" element={<AdminForgotPassword />} />
            </Route>

            {/* Cataloging page — protected but NO chrome (mobile full-screen UX) */}
            <Route
              path="catalog"
              element={
                <ProtectedRoute
                  allowedRoles={['tenant_owner', 'tenant_staff', 'super_admin']}
                  signInPath="/admin"
                >
                  <AdminCatalog />
                </ProtectedRoute>
              }
            />

            {/* The lot grid — protected but NO chrome. Jim, 2026-10-06: it is a
                pop-out window, so the office can keep the photos on one screen
                and the spreadsheet on the other. A side menu would eat the
                width the columns need. */}
            <Route
              path="auctions/:id/grid"
              element={
                <ProtectedRoute
                  allowedRoles={['tenant_owner', 'tenant_staff', 'super_admin']}
                  signInPath="/admin"
                >
                  <AdminLotGrid />
                </ProtectedRoute>
              }
            />

            {/* Print surface — protected but NO chrome (opens in a new tab and
                auto-prints; the nav must not render or it prints too). */}
            <Route
              path="invoices/:id/print"
              element={
                <ProtectedRoute
                  allowedRoles={['tenant_owner', 'tenant_staff', 'super_admin']}
                  signInPath="/admin"
                >
                  <InvoicePrint />
                </ProtectedRoute>
              }
            />

            {/* Authenticated pages — AdminChromeLayout (nav + outlet) */}
            <Route
              element={
                <ProtectedRoute
                  allowedRoles={['tenant_owner', 'tenant_staff', 'super_admin']}
                  signInPath="/admin"
                >
                  <AdminChromeLayout />
                </ProtectedRoute>
              }
            >
              <Route path="dashboard" element={<AdminDashboard />} />
              <Route path="onboarding" element={<AdminOnboarding />} />
              <Route path="onboarding/bidders" element={<AdminBidderImport />} />
              <Route path="brief" element={<AdminBrief />} />
              <Route path="copilot" element={<AdminCopilot />} />
              <Route path="copilot/memory" element={<AdminCopilotMemory />} />
              <Route path="auctions" element={<AdminAuctionList />} />
              <Route path="auctions/:id" element={<AdminAuctionDetail />} />
              <Route path="auctions/:id/approvals" element={<AdminAuctionApprovals />} />
              <Route path="auctions/:id/lots/:lotId" element={<AdminLotDetail />} />
              <Route path="billing" element={<AdminBilling />} />
              <Route path="invoices" element={<AdminInvoices />} />
              <Route path="invoices/failed-report" element={<AdminInvoiceFailedReport />} />
              <Route path="invoices/:id" element={<AdminInvoiceDetail />} />
              <Route path="ai-settings" element={<AdminAiSettings />} />
              <Route path="invoice-settings" element={<AdminInvoiceSettings />} />
              {/* Listing Prices moved into AI Settings → Pricing (2026-10-06); old links land there. */}
              <Route
                path="listing-prices"
                element={<Navigate to={LISTING_PRICE_SETTINGS_PATH} replace />}
              />
              <Route path="house-contact" element={<AdminHouseContact />} />
              <Route path="branding" element={<AdminBranding />} />
              <Route path="sales-intel" element={<AdminSalesIntel />} />
              <Route path="reports" element={<AdminReports />} />
              <Route path="inventory" element={<AdminInventoryList />} />
              <Route path="inventory/import" element={<AdminInventoryImport />} />
              <Route path="inventory/:id" element={<AdminInventoryDetail />} />
              <Route path="taxonomies" element={<AdminTaxonomies />} />
              <Route path="consignment-profiles" element={<AdminConsignmentProfiles />} />
              <Route path="tax-profiles" element={<AdminTaxProfiles />} />
              <Route path="consignors" element={<AdminConsignors />} />
              <Route path="consignors/:id" element={<AdminConsignorDetail />} />
              <Route path="bidders" element={<AdminBidders />} />
              <Route path="bidders/:id" element={<AdminBidderDetail />} />
              <Route path="questions" element={<AdminLotQuestions />} />
              <Route path="auctioneer-keys" element={<AdminAuctioneerKeys />} />
              <Route path="auctions/:id/auctioneer" element={<AdminAuctioneerConsole />} />
            </Route>
          </Route>

          {/* Super admin surface */}
          <Route path="super" element={<SuperAdminLayout />}>
            <Route index element={<SuperSignIn />} />
            <Route
              path="dashboard"
              element={
                <ProtectedRoute allowedRoles={['super_admin']} signInPath="/super">
                  <SuperDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="intelligence/engine-telemetry"
              element={
                <ProtectedRoute allowedRoles={['super_admin']} signInPath="/super">
                  <SuperEngineTelemetry />
                </ProtectedRoute>
              }
            />
            <Route
              path="intelligence/errors"
              element={
                <ProtectedRoute allowedRoles={['super_admin']} signInPath="/super">
                  <SuperErrorClusters />
                </ProtectedRoute>
              }
            />
            <Route
              path="intelligence/sales"
              element={
                <ProtectedRoute allowedRoles={['super_admin']} signInPath="/super">
                  <SuperSalesAnalytics />
                </ProtectedRoute>
              }
            />
            <Route
              path="intelligence/competitors"
              element={
                <ProtectedRoute allowedRoles={['super_admin']} signInPath="/super">
                  <SuperCompetitorResearch />
                </ProtectedRoute>
              }
            />
            <Route
              path="intelligence/predictions"
              element={
                <ProtectedRoute allowedRoles={['super_admin']} signInPath="/super">
                  <SuperPricingPredictions />
                </ProtectedRoute>
              }
            />
            <Route
              path="intelligence/churn"
              element={
                <ProtectedRoute allowedRoles={['super_admin']} signInPath="/super">
                  <SuperChurnSignals />
                </ProtectedRoute>
              }
            />
            <Route
              path="intelligence/briefs"
              element={
                <ProtectedRoute allowedRoles={['super_admin']} signInPath="/super">
                  <SuperBriefs />
                </ProtectedRoute>
              }
            />
            <Route
              path="copilot-gaps"
              element={
                <ProtectedRoute allowedRoles={['super_admin']} signInPath="/super">
                  <SuperCopilotGaps />
                </ProtectedRoute>
              }
            />
            {/* Where "Was this helpful?" lands. The control in the app exists
                because this page does — see SuperHelpFeedback. */}
            <Route
              path="help-feedback"
              element={
                <ProtectedRoute allowedRoles={['super_admin']} signInPath="/super">
                  <SuperHelpFeedback />
                </ProtectedRoute>
              }
            />
            {/* Where the Cataloging app's diagnostic reports land (Jim,
                2026-10-05: "Route the reports to the Super Tenant Dash"). */}
            <Route
              path="app-reports"
              element={
                <ProtectedRoute allowedRoles={['super_admin']} signInPath="/super">
                  <SuperAppReports />
                </ProtectedRoute>
              }
            />
            <Route
              path="onboarding-reset"
              element={
                <ProtectedRoute allowedRoles={['super_admin']} signInPath="/super">
                  <SuperOnboardingReset />
                </ProtectedRoute>
              }
            />
            {/* Onboarding step 0 — the only path by which a tenant comes into
                existence. Super-admin only: this mints a house AND an account. */}
            <Route
              path="tenants/new"
              element={
                <ProtectedRoute allowedRoles={['super_admin']} signInPath="/super">
                  <SuperTenantInvites />
                </ProtectedRoute>
              }
            />
            <Route path="forgot-password" element={<SuperForgotPassword />} />
          </Route>

          {/* Catch-all */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </ToastProvider>
    </BrowserRouter>
  );
}
